import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import AppShell from "@/components/layout/AppShell";
import DailyRecommendations from "@/components/dashboard/DailyRecommendations";
import { UsagePanel } from "@/components/UsagePanel";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import {
  Sparkles, Share2, CalendarClock, LineChart as LineIcon, CheckCircle2,
  Users, Heart, Video, TrendingUp, ExternalLink, RefreshCw, BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";

/* -------------------------------------------------------------------------- */
/*  Connected-state KPI card                                                  */
/* -------------------------------------------------------------------------- */
function LiveKpiCard({ label, value, Icon, testId, subtitle }) {
  const display = value === null || value === undefined ? "—" : Number(value).toLocaleString();
  return (
    <div data-testid={testId} className="nx-card p-5 relative overflow-hidden group hover:-translate-y-0.5 transition-transform">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="font-display text-3xl md:text-4xl mt-2 tabular-nums">{display}</div>
          {subtitle && <div className="mt-1 text-xs text-muted-foreground">{subtitle}</div>}
        </div>
        <div className="w-9 h-9 rounded-sm bg-primary/10 text-primary flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="absolute -end-8 -bottom-8 w-24 h-24 rounded-full bg-primary/5 group-hover:bg-primary/10 transition" />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Empty onboarding state (unchanged from Sprint 8)                          */
/* -------------------------------------------------------------------------- */
function EmptyOnboarding({ lang, nav }) {
  const steps = lang === "ar"
    ? [
        { icon: Share2, title: "١. اربط حساباتك", desc: "ربط انستقرام، تيك توك، تويتر، لينكدإن أو يوتيوب — بنقرة واحدة." },
        { icon: CalendarClock, title: "٢. أنشئ وجدول", desc: "استخدم استوديو المحتوى لكتابة، ورفع الوسائط، وجدولة النشر." },
        { icon: LineIcon, title: "٣. راقب النمو", desc: "شاهد التحليلات الحقيقية والتقارير الأسبوعية تظهر تلقائيًا." },
      ]
    : [
        { icon: Share2, title: "1. Connect your accounts", desc: "Link Instagram, TikTok, X, LinkedIn or YouTube in one click." },
        { icon: CalendarClock, title: "2. Create & schedule", desc: "Use Content Studio to draft, upload media and schedule posts." },
        { icon: LineIcon, title: "3. Watch growth", desc: "Real analytics and weekly reports appear automatically." },
      ];

  const chartTitles = lang === "ar"
    ? { followers: "نمو المتابعين", engagement: "التفاعل الأسبوعي" }
    : { followers: "Followers growth", engagement: "Weekly engagement" };

  return (
    <div data-testid="dashboard-empty-state" className="nx-card p-8 md:p-14 max-w-5xl mx-auto animate-fade-in-up">
      <div className="text-center mb-10">
        <div className="w-20 h-20 rounded-sm bg-primary/5 text-primary flex items-center justify-center mx-auto mb-6 relative">
          <Sparkles className="w-9 h-9" />
          <div className="absolute -inset-2 rounded-sm border border-primary/10 animate-pulse" />
        </div>
        <div className="text-xs uppercase tracking-widest text-primary/70 mb-3">
          {lang === "ar" ? "ابدأ رحلتك مع DAAM" : "Start your DAAM journey"}
        </div>
        <h2 data-testid="dashboard-empty-headline" className="font-display text-3xl md:text-4xl mb-4">
          {lang === "ar" ? "لا توجد بيانات بعد" : "No data yet"}
        </h2>
        <p className="text-muted-foreground max-w-xl mx-auto leading-relaxed">
          {lang === "ar"
            ? "لن نعرض أرقامًا وهمية. اربط أول حساب اجتماعي لك وستمتلئ لوحتك بتحليلات حقيقية فورية."
            : "We won't show fake numbers. Connect your first account and this dashboard fills with real, live insights."}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        {steps.map((s, i) => (
          <div key={i} data-testid={`onboarding-step-${i + 1}`} className="p-5 border border-border/60 rounded-sm hover:border-primary/40 transition-colors">
            <div className="w-10 h-10 rounded-sm bg-primary/5 text-primary flex items-center justify-center mb-4">
              <s.icon className="w-5 h-5" />
            </div>
            <div className="font-display text-lg mb-2">{s.title}</div>
            <div className="text-sm text-muted-foreground leading-relaxed">{s.desc}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        <div data-testid="empty-chart-followers" className="p-5 border border-dashed border-border/70 rounded-sm">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{chartTitles.followers}</div>
          <div className="h-40 relative">
            <ResponsiveContainer>
              <AreaChart data={[]}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="week" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Area type="monotone" dataKey="value" stroke="#002FA7" fill="none" />
              </AreaChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-xs uppercase tracking-widest text-muted-foreground/70">{lang === "ar" ? "لا توجد بيانات" : "No data"}</span>
            </div>
          </div>
        </div>
        <div data-testid="empty-chart-engagement" className="p-5 border border-dashed border-border/70 rounded-sm">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{chartTitles.engagement}</div>
          <div className="h-40 relative">
            <ResponsiveContainer>
              <BarChart data={[]}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Bar dataKey="value" fill="none" />
              </BarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-xs uppercase tracking-widest text-muted-foreground/70">{lang === "ar" ? "لا توجد بيانات" : "No data"}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
        <button data-testid="dashboard-connect-cta" onClick={() => nav("/app/social")} className="nx-btn-accent inline-flex items-center gap-2 w-full sm:w-auto justify-center">
          <Share2 className="w-4 h-4" /> {lang === "ar" ? "اربط حساباتك الآن" : "Connect a social account"}
        </button>
        <button data-testid="dashboard-open-assistant-cta" onClick={() => nav("/app/assistant")} className="nx-btn inline-flex items-center gap-2 w-full sm:w-auto justify-center">
          <Sparkles className="w-4 h-4" /> {lang === "ar" ? "تحدث مع المساعد الذكي" : "Chat with AI Assistant"}
        </button>
      </div>

      <div className="mt-8 pt-6 border-t border-border/50 flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
        {lang === "ar" ? "بيانات حقيقية فقط — لا توجد أرقام تجريبية." : "Real data only — no demo numbers."}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Connected TikTok section — shows real live account stats                  */
/* -------------------------------------------------------------------------- */
function ConnectedTikTok({ tt, lang, onSync, syncing }) {
  const last = tt.last_synced_at
    ? new Date(tt.last_synced_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US")
    : (lang === "ar" ? "لم تتم المزامنة بعد" : "not synced yet");

  return (
    <div className="nx-card p-6 mb-6" data-testid="tiktok-live-panel">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-14 h-14 rounded-sm overflow-hidden bg-black flex items-center justify-center shrink-0">
          {tt.avatar_url
            ? <img src={tt.avatar_url} alt="" className="w-full h-full object-cover" />
            : <span className="text-white font-display">TT</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-display text-xl truncate">{tt.display_name || tt.username || "TikTok"}</span>
            {tt.is_verified && <BadgeCheck className="w-4 h-4 text-blue-500" />}
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 bg-emerald-500/10 text-emerald-600 border border-emerald-500/30 rounded-sm">
              <CheckCircle2 className="w-2.5 h-2.5" /> {lang === "ar" ? "متصل" : "Live"}
            </span>
          </div>
          {tt.username && <div className="text-xs text-muted-foreground">@{tt.username}</div>}
          <div className="text-[10px] text-muted-foreground mt-1">
            {lang === "ar" ? "آخر مزامنة: " : "Last synced: "}{last}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {tt.profile_deep_link && (
            <a href={tt.profile_deep_link} target="_blank" rel="noreferrer" data-testid="tiktok-open-profile"
               className="inline-flex items-center gap-1.5 text-xs border border-border/60 rounded-sm px-3 py-1.5 hover:border-primary/40">
              {lang === "ar" ? "فتح الحساب" : "Open profile"} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          <button data-testid="tiktok-sync-btn" onClick={onSync} disabled={syncing}
                  className="inline-flex items-center gap-1.5 text-xs border border-border/60 rounded-sm px-3 py-1.5 hover:border-primary/40 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing
              ? (lang === "ar" ? "جاري..." : "Syncing…")
              : (lang === "ar" ? "مزامنة الآن" : "Sync now")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main Dashboard                                                            */
/* -------------------------------------------------------------------------- */
export default function DashboardPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();

  const [analytics, setAnalytics] = useState(null);
  const [tiktok, setTiktok] = useState(null); // {connected, account} | {connected:false}
  const [ready, setReady] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    const [a, t] = await Promise.allSettled([
      api.get("/analytics/detailed"),
      api.get("/tiktok/me"),
    ]);
    if (a.status === "fulfilled") setAnalytics(a.value.data);
    if (t.status === "fulfilled") setTiktok(t.value.data);
    setReady(true);
  };
  useEffect(() => { load(); }, []);

  const doSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/tiktok/sync");
      setTiktok({ connected: true, account: data.account });
      // Re-pull analytics so KPI cards reflect the fresh numbers
      const { data: a } = await api.get("/analytics/detailed");
      setAnalytics(a);
      toast.success(lang === "ar" ? "تم تحديث بيانات TikTok" : "TikTok data refreshed");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message_ar || (lang === "ar" ? "تعذّرت المزامنة" : "Sync failed"));
    } finally {
      setSyncing(false);
    }
  };

  if (!ready) {
    return (
      <AppShell>
        <div className="p-12 text-center text-muted-foreground">{t.common.loading}</div>
      </AppShell>
    );
  }

  const hasLive = analytics?.has_data === true;
  const tt = tiktok?.connected ? tiktok.account : null;
  const kpis = analytics?.kpis || {};

  return (
    <AppShell>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          {lang === "ar" ? "مرحباً" : "Welcome"}
        </div>
        <h1 className="font-display text-3xl md:text-4xl mt-1">{user?.name}</h1>
      </div>

      {/* DAAM Intelligence: daily AI recommendations */}
      <DailyRecommendations />

      {/* Per-plan usage snapshot (Critical Task 4) */}
      <UsagePanel compact />

      {hasLive ? (
        <div data-testid="dashboard-live" className="animate-fade-in-up">
          {tt && <ConnectedTikTok tt={tt} lang={lang} onSync={doSync} syncing={syncing} />}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <LiveKpiCard
              testId="kpi-followers"
              label={lang === "ar" ? "المتابعون" : "Followers"}
              value={kpis.followers}
              Icon={Users}
              subtitle={tt ? "TikTok" : null}
            />
            <LiveKpiCard
              testId="kpi-likes"
              label={lang === "ar" ? "الإعجابات" : "Likes"}
              value={kpis.likes}
              Icon={Heart}
              subtitle={tt ? "TikTok" : null}
            />
            <LiveKpiCard
              testId="kpi-videos"
              label={lang === "ar" ? "الفيديوهات" : "Videos"}
              value={kpis.videos}
              Icon={Video}
              subtitle={tt ? "TikTok" : null}
            />
            <LiveKpiCard
              testId="kpi-engagement"
              label={lang === "ar" ? "نسبة التفاعل" : "Engagement rate"}
              value={kpis.engagement_rate}
              Icon={TrendingUp}
              subtitle={lang === "ar" ? "الإعجابات ÷ المتابعون" : "Likes ÷ Followers"}
            />
          </div>

          <div className="nx-card p-6" data-testid="dashboard-live-note">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
              {lang === "ar" ? "مصدر البيانات" : "Data source"}
            </div>
            <div className="text-sm text-foreground/85 leading-relaxed">
              {lang === "ar"
                ? "هذه أرقام حقيقية مأخوذة مباشرة من TikTok عبر Login Kit. الرسوم البيانية الزمنية والوصول لكل فيديو ستُضاف تلقائيًا بمجرد تفعيل مزامنة الفيديو الدورية."
                : "These numbers are live from TikTok via the official Login Kit. Time-series charts and per-video reach will appear automatically once the periodic video sync ships."}
            </div>
          </div>
        </div>
      ) : (
        <EmptyOnboarding lang={lang} nav={nav} />
      )}
    </AppShell>
  );
}
